import React, { useEffect, useMemo, useState } from 'react';

import PageWrapper from '../PageWrapper';
import Page from '../Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleAccordion,
  BubbleAlert,
  BubbleBadge,
  BubbleButton,
  BubbleCheckbox,
  BubbleCheckboxGroup,
  BubbleDebounce,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleProgressBar,
  BubbleTable,
  BubbleTooltip,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import ReserveNavigation from '@go-client/components/Navigations/ReserveNavigation';
import ReserveSubNavigation from '@go-client/components/Navigations/ReserveSubNavigation';
import ReservationPath, {
  ReservationFormMode,
} from '@go-client/components/ReservationPath/ReservationPath';
import { ReservableEventTemplateSnapshot } from '@go-shared/types/reservable';
import {
  EnhancedReservationStatus,
  Reservation,
  ReservationAnalytics,
  ReservationAnalyticsTallyItem,
  ReservationAttendance,
} from '@go-shared/types/reservation';
import {
  ReserveTimeline,
  ReserveTimelineEvent,
  ReserveTimelineItem,
} from '@go-shared/types/reserve';
import {
  findOngoing,
  get as getReservation,
  changeAttendance,
} from '@go-client/lib/api/reservation';
import { toggleSlotException } from '@go-client/lib/api/reservable';
import {
  dateToIsoUTC,
  isIsoDate,
  getLocaleDateStringFromIso,
  addDaysToIsoDate,
} from '@go-shared/lib/time';
import { getHourFormat } from '@go-shared/lib/property';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import {
  hashStringToColor,
  hasOwnProperty,
  makeFullName,
  safeIdCompare,
  splitEverySecondWord,
  formatCentsToUSD,
} from '@go-shared/lib/utils';
import { firstOf, groupBy, mapBy, notUndefined, onlyUnique } from '@go-shared/lib/functional';
import { getSimpleUsers } from '@go-client/lib/api/user';
import { SimpleUser, User } from '@go-shared/types/user';
import qslib from '@go-shared/lib/querystring';
import { BareEventTemplate } from '@go-shared/types/eventTemplate';
import { BubbleBadgeProps } from '@go-client/components/BubbleUI/BubbleBadge';
import {
  buildHostTimeSlotKeyPart,
  buildReserveTimeslotLabel,
  buildTimeSlotKeyPart,
  calculateReservationAnalytics,
  DEFAULT_RENTAL_KEY,
  experienceIsRental,
  getSlotSpaceStatus,
  populateReserveTimeline,
  slotKeyFromReserveTimelineItem,
  slotKeyWithEvent,
  reserveTimeSorter,
} from '@go-shared/lib/reserve';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { Time } from '@go-shared/types/time';
import {
  makeReservationTableFields,
  makeReservationTableItem,
  makeReservationTableTemplates,
} from './ReserveTransactions';
import {
  canCreateSomeReservations,
  canHostEvents,
  canManageAllReservations,
  canManageReservation,
  canManageTimelineItem,
} from '@go-shared/lib/permissions';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';

const DEFAULT_ONGOING_DAYS_BEFORE = 0;
const DEFAULT_ONGOING_DAYS_AFTER = 7;

interface ReserveOngoingParams {
  property: string;
}

interface ListViewTableItem {
  id: string;
  eventTitle: string;
  date: string;
  label?: string;
  timeLabel: string;
  host?: string;
  rental?: string;
  reservationCount: number;
  capacity: number;
  seatsTaken: number;
  directSeatsTaken: number;
  isRental: boolean;
  isExceptionDate: boolean;
  allowOverfill: boolean;
  dayspanCapacityIsInaccurate: boolean;

  color: string;
  isFirstRow: boolean;
  isLastRow: boolean;
  totalNumRows: number;
  isExpandedRow: boolean;
  hasExpandedRow: boolean;
  reservations: ReservationTableItem[];
  analytics: ReservationAnalytics;

  _slot: ReserveTimelineItem;
  _firstSlot: ReserveTimelineItem;
  _event: BareEventTemplate;
  _rentalMatrix?: RentalMatrix;
}

interface GridViewTableItem {
  id: string;
  time: string;
  label?: string;
  isLastInGroup?: boolean;
  [key: string]: ListViewTableItem | string | boolean | undefined;
}

function gridViewCellIsListViewTableItem(
  cell: ListViewTableItem | string | boolean | undefined
): cell is ListViewTableItem {
  return typeof cell === 'object' && cell !== null && 'id' in cell;
}

export interface ReservationTableItem<T extends Reservation = Reservation> {
  id: string;
  key: string;
  event: string;
  date: string;
  time: string;
  host?: string;
  rental?: string;
  status: EnhancedReservationStatus;
  cartCode: string;
  reservationCode: string;
  name: string;
  email: string;
  phone: string;
  statusVariant: BubbleBadgeProps['variant'];
  attendance?: ReservationAttendance;
  attendanceVariant: BubbleBadgeProps['variant'];
  isFlattened: boolean;
  isIndirect: boolean;
  isReleased: boolean;
  isCancelled: boolean;
  isOccurrence: boolean;
  totalOccurrences: number;
  sourceReservationId: string;
  analytics: ReservationAnalytics;
  _reservation: T;
  _event: ReservableEventTemplateSnapshot;
}

export interface ReservationTallyItem {
  id: string;
  name: string;
  total: number;
  typeTotal: number;
  isExpanded?: boolean;
  _tally: ReservationAnalyticsTallyItem;
}

interface RentalMatrixItem {
  id: string;
  time: string;
  timeLabel?: string;
  host?: string;
  isExceptionDate: boolean;
  slot?: ReserveTimelineItem;
  cells: Record<string, RentalMatrixCell>;
}

interface RentalMatrixCell {
  seatsTaken: number;
  directSeatsTaken: number;
  allowOverfill: boolean;
  description: string;
  capacity: number;
  time: Time;
  duration?: number;
  slot?: ReserveTimelineItem;
  isExceptionDate: boolean;
  reservations: ReservationTableItem[];
}

interface RentalMatrix {
  columnOpts: Array<{ key: string; label: string }>;
  rowOpts: Array<{
    key: string;
    label: string;
    timeLabel?: string;
    hostLabel?: string;
    host?: string;
    isExceptionDate: boolean;
  }>;
  cellMatrix: Record<string, Record<string, RentalMatrixCell>>;
  summaryCapacity: number;
  summarySeatsTaken: number;
  allReservations: ReservationTableItem[];
}

interface RentalMatrixCellCoord {
  rowKey: string;
  colKey: string;
}

interface ListViewTableItemGroup {
  id: string;
  date: string;
  color: string;
  items: ListViewTableItem[];
}

interface GridViewTableItemGroup {
  id: string;
  name: string;
  color: string;
  allTimeslotFilterOptions: Array<{ id: string; label: string }>;
  items: GridViewTableItem[];
}

interface OngoingFilters {
  selectedExperiences: Array<BubbleTypeaheadValue<BareEventTemplate>>;
  selectedHosts: Array<BubbleTypeaheadValue<SimpleUser | null>>;
  groupByEvent: boolean;
  daysBySide: boolean;
  hideEmptyDays: boolean;
  hideEmptySlots: boolean;
  selectedDayOnly: boolean;
  showArchived: boolean;
  showReleased: boolean;
  showIndirect: boolean;
}

type ListViewTableFields = BubbleTableFields<ListViewTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

type GridViewTableFields = BubbleTableFields<GridViewTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

export type ReservationTableFields = BubbleTableFields<ReservationTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

function makeDefaultFilters(user: User) {
  const selectedHosts: Array<BubbleTypeaheadValue<User>> = [];

  if (canHostEvents(user)) {
    selectedHosts.push({ key: user._id, item: user, refKey: user._id });
  }

  const filters: OngoingFilters = {
    selectedExperiences: [],
    selectedHosts,
    groupByEvent: true,
    daysBySide: false,
    hideEmptyDays: true,
    hideEmptySlots: false,
    selectedDayOnly: false,
    showArchived: false,
    showReleased: false,
    showIndirect: true,
  };

  return filters;
}
interface OngoingQueryStrings {
  date: string;
  daysBefore: number;
  daysAfter: number;
  reservation?: string;
}

function buildRentalMatrix(groupedItems: ListViewTableItem[]): RentalMatrix {
  const columnOpts: RentalMatrix['columnOpts'] = [];
  const rowOpts: RentalMatrix['rowOpts'] = [];
  const cellMatrix: RentalMatrix['cellMatrix'] = {};
  const allReservations = groupedItems.flatMap(item => item.reservations);

  let summaryCapacity = 0;
  let summarySeatsTaken = 0;
  let hasInternalOverfills = false;

  groupedItems.forEach(itm => {
    const { host, rental, time, timeDisplay, duration, dayspan } = itm._slot;

    const colKey = rental || DEFAULT_RENTAL_KEY;
    const colLabel = itm.rental || 'Unassigned';

    const rowKey = buildHostTimeSlotKeyPart(host, time, duration);
    const rowLabel = buildReserveTimeslotLabel({
      time,
      timeDisplay,
      duration,
      dayspan,
    });

    if (!columnOpts.some(c => c.key === colKey)) {
      columnOpts.push({ key: colKey, label: colLabel });
    }

    if (!rowOpts.some(r => r.key === rowKey)) {
      rowOpts.push({
        key: rowKey,
        label: rowLabel,
        timeLabel: itm.label,
        hostLabel: itm.host,
        host,
        isExceptionDate: itm.isExceptionDate,
      });
    }

    if (!cellMatrix[rowKey]) {
      cellMatrix[rowKey] = {};
    }

    cellMatrix[rowKey][colKey] = {
      seatsTaken: itm.seatsTaken,
      directSeatsTaken: itm.directSeatsTaken,
      capacity: itm.capacity,
      allowOverfill: itm.allowOverfill,
      time,
      duration,
      description: buildReserveTimeslotLabel({
        label: itm.label,
        time,
        timeDisplay: itm._slot.timeDisplay,
        duration,
        dayspan: itm._slot.dayspan,
        host: itm.host,
        rental: colLabel,
      }),
      slot: itm._slot,
      isExceptionDate: itm.isExceptionDate,
      reservations: itm.reservations,
    };

    hasInternalOverfills =
      (itm.seatsTaken > itm.capacity && !itm.allowOverfill) || hasInternalOverfills;

    summarySeatsTaken += itm.seatsTaken;
    summaryCapacity += itm.capacity;
  });

  return {
    columnOpts,
    rowOpts,
    cellMatrix,
    // if there are internal overfills, we set summary capacity to 0 to trigger red styling in the summary row
    summaryCapacity: hasInternalOverfills ? 0 : summaryCapacity,
    summarySeatsTaken,
    allReservations,
  };
}

function getQueryStrings(defaultOngoingDate: string): OngoingQueryStrings {
  const qs = qslib.readQs();

  const daysBefore =
    hasOwnProperty(qs, 'daysBefore') && typeof qs.daysBefore === 'number'
      ? qs.daysBefore
      : DEFAULT_ONGOING_DAYS_BEFORE;
  const daysAfter =
    hasOwnProperty(qs, 'daysAfter') && typeof qs.daysAfter === 'number'
      ? qs.daysAfter
      : DEFAULT_ONGOING_DAYS_AFTER;
  const date =
    hasOwnProperty(qs, 'date') && typeof qs.date === 'string' ? qs.date : defaultOngoingDate;
  const reservation =
    hasOwnProperty(qs, 'reservation') && typeof qs.reservation === 'string'
      ? qs.reservation
      : undefined;

  return { daysBefore, daysAfter, date, reservation };
}

type ReserveOngoingProps = RouteComponentProps<ReserveOngoingParams>;

export default function ReserveOngoing(props: ReserveOngoingProps) {
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();
  const { serverDate } = usePublicServerInfo();

  const [queryStrings, setQueryStrings] = useState<OngoingQueryStrings>(
    getQueryStrings(serverDate.iso)
  );
  const [loading, setLoading] = useState(true);
  const [changingException, setChangingException] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [mode, _setMode] = useState<ReservationFormMode | undefined>(undefined);

  const [users, setUsers] = useState<SimpleUser[]>([]);
  const [ongoings, setOngoings] = useState<ReserveTimeline[]>([]); // New state for schedules
  const [expandedRow, setExpandedRow] =
    useState<{ rowKey: string; date: string } | undefined>(undefined);
  const [filters, setFilters] = useState<OngoingFilters>(makeDefaultFilters(user));
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  const [showMoreTicketDetails, setShowMoreTicketDetails] = useState(false);
  const [showMorePackageDetails, setShowMorePackageDetails] = useState(false);
  const [showMoreAddonDetails, setShowMoreAddonDetails] = useState(false);
  const [reservationSearch, setReservationSearch] = useState('');
  const [showReservationList, setShowReservationList] = useState(false);

  const [activeMatrixCellCoord, setActiveMatrixCellCoord] =
    useState<RentalMatrixCellCoord | undefined>(undefined);

  const hourFormat = getHourFormat(activeProperty);
  const usersDict = mapBy(users, '_id');

  async function fetchData() {
    setLoading(true);

    const startDate = isIsoDate(queryStrings.date) ? queryStrings.date : serverDate.iso;

    const sailingsFromDate = addDaysToIsoDate(startDate, -queryStrings.daysBefore);
    const sailingsToDate = addDaysToIsoDate(startDate, queryStrings.daysAfter);

    try {
      const [users, ongoings] = await Promise.all([
        getSimpleUsers(activeProperty._id),
        findOngoing(
          activeProperty._id,
          sailingsFromDate,
          sailingsToDate,
          filters.showArchived,
          filters.showReleased
        ),
      ]);

      setUsers(users.filter(canHostEvents));

      // repopulate ongoing for ease of use
      const eventTemplateMap = mapBy(ongoings.eventTemplates, '_id');
      setOngoings(ongoings.timeline.map(ti => populateReserveTimeline(ti, eventTemplateMap)));
      refreshSelectedReservation().catch(console.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData().catch(console.error);
  }, [queryStrings, activeProperty, filters.showArchived, filters.showReleased]);

  useEffect(() => {
    setActiveMatrixCellCoord(undefined);
  }, [expandedRow]);

  function toggleExpandedRow(rowKey: string, date: string) {
    setExpandedRow(prev =>
      prev?.rowKey === rowKey && prev?.date === date ? undefined : { rowKey: rowKey, date }
    );
  }

  async function refreshSelectedReservation() {
    const parsedQs = getQueryStrings(serverDate.iso);

    const toSelect = parsedQs.reservation;
    if (!toSelect) return;

    const reservation = await getReservation(activeProperty._id, toSelect);

    handleOpenReservationPanel({ type: 'edit', edit: reservation });
  }

  async function handleEditReservationById(id: string) {
    // Always fetch fresh from server so flattened occurrence facsimiles don't
    // bleed their overridden date/time into the edit form.
    qslib.writeQs({ reservation: id });
    await refreshSelectedReservation();
  }

  function handleOpenReservationPanel(newMode: ReservationFormMode) {
    _setMode(newMode);
    if (newMode.type === 'edit') {
      qslib.writeQs({ reservation: newMode.edit._id });
    }
  }

  function handleCloseReservationPanel() {
    _setMode(undefined);
    qslib.writeQs({});
  }

  function handleOnAddDaysBefore(amt: number) {
    const newQueryStrings = { ...queryStrings, daysBefore: queryStrings.daysBefore + amt };

    qslib.writeQs(newQueryStrings);

    setQueryStrings(newQueryStrings);
  }

  function handleOnAddDaysAfter(amt: number) {
    const newQueryStrings = { ...queryStrings, daysAfter: queryStrings.daysAfter + amt };

    qslib.writeQs(newQueryStrings);

    setQueryStrings(newQueryStrings);
  }

  async function handleOnChangeDate(iso: string) {
    const newQueryStrings = { ...queryStrings, date: iso };

    qslib.writeQs(newQueryStrings);

    setQueryStrings(newQueryStrings);
  }

  function buildRentalsDict(
    timelineEvent: ReserveTimelineEvent,
    timelineItems: ReserveTimelineItem[]
  ) {
    const rentalsDict: Record<string, string> = {};

    (timelineEvent.template.reservables?.tickets ?? []).forEach(ticket => {
      if (!rentalsDict[ticket._id]) {
        rentalsDict[ticket._id] = ticket.name;
      }
    });

    timelineItems.forEach(slot => {
      slot.directReservations.forEach(res => {
        res.eventSnapshot.reservables.tickets.forEach(ticket => {
          if (!rentalsDict[ticket._id]) {
            rentalsDict[ticket._id] = ticket.name;
          }
        });
      });
    });

    return rentalsDict;
  }

  function makeListViewTableItem(
    timelineItems: ReserveTimelineItem[],
    timelineEvent: ReserveTimelineEvent,
    date: string,
    rentalsDict: Record<string, string>
  ): ListViewTableItem[] {
    return timelineItems.map((slot, i) => {
      const reservations = [
        ...slot.directReservations,
        ...(filters.showIndirect ? slot.indirectReservations : []),
      ];

      const indirectReservationIds = slot.indirectReservations.map(r => r._id).filter(onlyUnique);

      const isExceptionDate =
        (slot.exceptions ?? []).findIndex(ex => ex.date === date && ex.rental === slot.rental) > -1;

      const { dynamicCapacity } = getSlotSpaceStatus(slot);

      const item: ListViewTableItem = {
        id: slotKeyWithEvent(timelineEvent.template._id, slot.slotKey),
        isRental: false,
        eventTitle: timelineEvent.template.title,
        date,
        label: slot.label,
        reservationCount: slot.directReservations.length,
        dayspanCapacityIsInaccurate: !!slot.dayspanCapacityIsInaccurate,
        timeLabel: buildReserveTimeslotLabel({
          time: slot.time,
          timeDisplay: slot.timeDisplay,
          duration: slot.duration,
          dayspan: slot.dayspan,
        }),
        reservations: reservations.map(res =>
          makeReservationTableItem(res, usersDict, hourFormat, indirectReservationIds, serverDate)
        ),
        host:
          slot.host && usersDict[slot.host]
            ? makeFullName(usersDict[slot.host].firstName, usersDict[slot.host].lastName)
            : undefined,
        rental: slot.rental ? rentalsDict[slot.rental] : undefined,
        capacity: dynamicCapacity,
        seatsTaken: slot.seatsTaken,
        directSeatsTaken: slot.directSeatsTaken,
        color: hashStringToColor(timelineEvent.template.title),
        isFirstRow: !filters.groupByEvent ? false : i === 0,
        isLastRow: !filters.groupByEvent ? false : i === timelineItems.length - 1,
        isExpandedRow: false,
        isExceptionDate,
        totalNumRows: timelineItems.length,
        hasExpandedRow: false,
        allowOverfill: slot.directSeatsTaken === 0,
        _firstSlot: timelineItems[0],
        _slot: slot,
        _event: timelineEvent.template,
        analytics: calculateReservationAnalytics(slot.directReservations),
      };

      return item;
    });
  }

  function condenseListViewTableItems(
    timelineItems: ReserveTimelineItem[],
    timelineEvent: ReserveTimelineEvent,
    date: string,
    listViewItems: ListViewTableItem[]
  ): ListViewTableItem[] {
    // we need to condense all these slotRows into one row - when expanded, we have
    // a grid to show the individual slots and their rentals/hosts. here we just build
    // a summary for the one row.

    const summaryId = `${timelineEvent.template._id}-${date}-summary`;

    const matrix = buildRentalMatrix(listViewItems);

    const hostCount = listViewItems
      .map(row => row.host)
      .filter(notUndefined)
      .filter(onlyUnique).length;
    const timeCount = Object.keys(matrix.rowOpts).length;
    const rentalsCount = Object.keys(matrix.columnOpts).length;

    const allDirectReservations = listViewItems
      .flatMap(row => row.reservations)
      .filter(r => !r.isIndirect);

    const summaryItem: ListViewTableItem = {
      id: summaryId,
      isRental: true,
      eventTitle: timelineEvent.template.title,
      dayspanCapacityIsInaccurate: listViewItems.some(row => row.dayspanCapacityIsInaccurate),
      date,
      label: timeCount > 0 ? `${timeCount} times` : undefined,
      timeLabel: '',
      reservationCount: allDirectReservations.length,
      reservations: allDirectReservations,
      host: hostCount > 0 ? `${hostCount} hosts` : undefined,
      rental: rentalsCount > 0 ? `${rentalsCount} rentals` : undefined,
      capacity: matrix.summaryCapacity,
      seatsTaken: matrix.summarySeatsTaken,
      directSeatsTaken: 1, // not used but we add to prevent striped
      color: listViewItems[0].color,
      isFirstRow: filters.groupByEvent,
      isLastRow: filters.groupByEvent,
      isExpandedRow: false,
      totalNumRows: 1,
      hasExpandedRow: false,
      isExceptionDate: false,
      allowOverfill: false,
      _firstSlot: timelineItems[0],
      _slot: timelineItems[0],
      _event: timelineEvent.template,
      analytics: calculateReservationAnalytics(
        listViewItems.flatMap(row => row._slot.directReservations)
      ),
      _rentalMatrix: matrix,
    };

    return [summaryItem];
  }

  const listViewTableGroupItems: ListViewTableItemGroup[] = useMemo(() => {
    if (viewMode !== 'list') return [];

    const items = ongoings
      .map(ongoing => {
        if (filters.selectedDayOnly && ongoing.date !== queryStrings.date) {
          return undefined;
        }

        const oItem: ListViewTableItemGroup = {
          id: ongoing.date,
          date: getLocaleDateStringFromIso(ongoing.date, {
            month: 'long',
            day: 'numeric',
            weekday: 'long',
          }),
          color: hashStringToColor(getLocaleDateStringFromIso(ongoing.date, { weekday: 'short' })),
          items: ongoing.events
            .filter(ev =>
              filters.selectedExperiences.length > 0
                ? filters.selectedExperiences.some(se =>
                    safeIdCompare(ev.template._id, se.item._id)
                  )
                : true
            )
            .flatMap(ev => {
              const isRental = experienceIsRental(ev.template.reservables?.experienceType);

              const items = ev.items
                .filter(s =>
                  filters.selectedHosts.length > 0
                    ? filters.selectedHosts.some(sh => safeIdCompare(sh.item?._id, s.host))
                    : true
                )
                .filter(s => (filters.hideEmptySlots ? s.directReservations.length > 0 : true));

              const rentalsDict = buildRentalsDict(ev, items);
              const slotRows = makeListViewTableItem(items, ev, ongoing.date, rentalsDict);

              return isRental && slotRows.length > 0
                ? condenseListViewTableItems(items, ev, ongoing.date, slotRows)
                : slotRows;
            })
            // events are sorted within themselves, but not yet sorted against other events
            .sort((a, b) => reserveTimeSorter(a._firstSlot, b._firstSlot)),
        };

        if (!filters.groupByEvent) {
          // kind of sucks, but now I have to sort again if the user does not want it grouped by group
          oItem.items.sort((a, b) => reserveTimeSorter(a._slot, b._slot));
        }

        return filters.hideEmptyDays && oItem.items.length < 1 ? undefined : oItem;
      })
      .filter(notUndefined);

    return items;
  }, [ongoings, filters, queryStrings.date, viewMode]);

  // cheap overlay pass: applies isExpandedRow/hasExpandedRow without rebuilding the
  // reservation analytics/rental matrices that listViewTableGroupItems computes above
  const expandedListViewTableGroupItems: ListViewTableItemGroup[] = useMemo(() => {
    return listViewTableGroupItems.map(group => {
      if (!filters.groupByEvent) {
        return {
          ...group,
          items: group.items.map(item =>
            item.id === expandedRow?.rowKey
              ? { ...item, isExpandedRow: true, hasExpandedRow: true }
              : item
          ),
        };
      }

      const items: ListViewTableItem[] = [];
      let i = 0;
      while (i < group.items.length) {
        const chunk = group.items.slice(i, i + group.items[i].totalNumRows);
        const expandedIdx = chunk.findIndex(it => it.id === expandedRow?.rowKey);
        chunk.forEach((it, idx) =>
          items.push({
            ...it,
            isExpandedRow: idx === expandedIdx,
            hasExpandedRow: expandedIdx > -1,
          })
        );
        i += chunk.length;
      }

      return { ...group, items };
    });
  }, [listViewTableGroupItems, expandedRow, filters.groupByEvent]);

  const listViewTableFields: ListViewTableFields[] = [
    {
      key: 'event',
      _visible: filters.groupByEvent,
      behavior: BubbleTableFieldBehavior.MinWidth,
      tdProps: {
        className: 'pr-3-i',
      },
    },
    {
      key: 'eventTitle',
      _visible: !filters.groupByEvent,
      label: 'Event',
      tdProps: {
        style: { width: '300px' },
      },
    },
    {
      key: 'time',
    },
    {
      key: 'host',
      label: 'Rental / Host',
    },
    {
      key: 'reservationCount',
      label: 'Reservations',
    },
    { key: 'revenue' },
    { key: 'stats', behavior: BubbleTableFieldBehavior.Condense },
    {
      key: 'capacity',
      label: 'Slots',
      tdProps: {
        className: 'whitespace-nowrap',
        style: { width: '200px' },
      },
    },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
      render() {
        return (
          <BubbleTooltip
            textRender={() => (
              <div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon="ban" fixedWidth className="mr-1" />
                  <div className="whitespace-nowrap">Block Slot</div>
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon="plus" fixedWidth className="mr-1" />
                  <div className="whitespace-nowrap">Add to Slot</div>
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon
                    icon="arrow-up-right-and-arrow-down-left-from-center"
                    fixedWidth
                    className="mr-1"
                  />
                  <div className="whitespace-nowrap">Show Details</div>
                </div>
              </div>
            )}
          >
            <div>
              Actions <FontAwesomeIcon icon="info-circle" />
            </div>
          </BubbleTooltip>
        );
      },
    },
  ]
    .filter(notUndefined)
    .filter(tf => (tf._visible !== undefined ? tf._visible : true));

  const listViewTableTemplates: BubbleTableTemplate<ListViewTableItem>[] = [
    {
      key: 'event',
      render: ({ isFirstRow, eventTitle }) =>
        isFirstRow ? (
          <div className="flex flex-col gap-3">
            <div>
              {splitEverySecondWord(eventTitle).map((v, i) => (
                <div className="w-32 truncate" key={i}>
                  {v}
                </div>
              ))}
            </div>
          </div>
        ) : null,
      tdProps: ({ isFirstRow, totalNumRows, hasExpandedRow, color }, fieldProps) => {
        const classes = ['border-b-2 align-top text-md font-semibold text-white relative'];
        if (fieldProps.className) classes.push(fieldProps.className);

        return isFirstRow
          ? {
              rowSpan: totalNumRows + (hasExpandedRow ? 1 : 0),
              className: classes.join(' '),
              style: {
                backgroundColor: color,
                borderColor: color,
              },
            }
          : { style: { display: 'none' } };
      },
    },
    {
      key: 'eventTitle',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: ({ eventTitle, color }) => (
        <div className="font-semibold" style={{ color }}>
          {eventTitle}
        </div>
      ),
    },
    {
      key: 'time',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: ({ timeLabel, label }) => (
        <div>
          {label && <div>{label}</div>}
          <div className={label ? 'text-xs text-slate' : ''}>{timeLabel}</div>
        </div>
      ),
    },
    {
      key: 'host',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: ({ host, rental }) =>
        (host || rental) && (
          <div>
            {rental && <div>{rental}</div>}
            <div className={`${rental ? 'text-xs text-slate' : ''}`}>{host}</div>
          </div>
        ),
    },
    {
      key: 'reservationCount',
      tdProps: makeDefaultListViewTemplateTdProps,
    },
    {
      key: 'revenue',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: data => (
        <span
          className={`${
            data.analytics.totalRevenue === 0
              ? ''
              : data.analytics.totalRevenue > 0
              ? 'font-bold text-lime-darkest'
              : 'font-bold text-crimson'
          }`}
        >
          {formatCentsToUSD(data.analytics.totalRevenue)}
        </span>
      ),
    },
    {
      key: 'stats',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: data => {
        return (
          <BubbleButton
            onClick={() => toggleExpandedRow(data.id, data.date)}
            variant="white"
            inverse
            className="p-0-i"
          >
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i"
              variant={data.analytics.numOfTickets > 0 ? 'lime' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="ticket" />
              <span>{data.analytics.numOfTickets}</span>
            </BubbleBadge>
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i"
              variant={data.analytics.numOfPackages > 0 ? 'banana' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="box-open" />
              <span>{data.analytics.numOfPackages}</span>
            </BubbleBadge>
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i"
              variant={data.analytics.numOfAddons > 0 ? 'ocean' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="puzzle-piece" />
              <span>{data.analytics.numOfAddons}</span>
            </BubbleBadge>
          </BubbleButton>
        );
      },
    },
    {
      key: 'capacity',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: ({
        capacity,
        seatsTaken,
        directSeatsTaken,
        allowOverfill,
        isExpandedRow,
        dayspanCapacityIsInaccurate,
      }) => {
        return (
          <BubbleProgressBar
            current={seatsTaken}
            linearFill="lime"
            max={capacity}
            unknown={dayspanCapacityIsInaccurate}
            striped={seatsTaken > 0 && directSeatsTaken === 0 && capacity != 0}
            allowOverfill={allowOverfill}
            active={isExpandedRow}
          />
        );
      },
    },
    {
      key: 'actions',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: data => (
        <BubbleOverlay
          loading={changingException}
          hideSpinner
          loadingClassName="rounded"
          className="text-right"
        >
          {!data.isRental && canManageTimelineItem(user, data._slot) && (
            <BubbleButton
              onClick={() => handleToggleException(data.date, data._event, data._slot)}
              className="p-2-i"
              variant={data.isExceptionDate ? 'crimson' : 'slate'}
              inverse
              icon="ban"
            />
          )}
          {canManageTimelineItem(user, data._slot) && (
            <BubbleButton
              onClick={() =>
                handleOpenReservationPanel({
                  type: 'create',
                  slot: !data.isRental ? data._slot : undefined,
                  event: data._event,
                  initialDate: data.date,
                })
              }
              className="p-2-i"
              variant="navy"
              inverse
              icon="plus"
            />
          )}
          <BubbleButton
            onClick={() => toggleExpandedRow(data.id, data.date)}
            className="p-2-i"
            variant="navy"
            inverse
            icon={
              expandedRow?.rowKey === data.id
                ? 'arrow-down-left-and-arrow-up-right-to-center'
                : 'arrow-up-right-and-arrow-down-left-from-center'
            }
          />
        </BubbleOverlay>
      ),
    },
  ];

  function makeDefaultListViewTemplateTdProps(
    data: ListViewTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const { isLastRow, isExpandedRow, color } = data;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        isLastRow && !isExpandedRow ? 'border-b-2' : undefined,
        isExpandedRow ? 'bg-bubblegum-lightest' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
      style: {
        ...(fieldTdProps.style || {}),
        borderBottomColor: isLastRow ? (color as string) : '',
      },
    };
  }

  const gridDates = ongoings.map(ongoing => ongoing.date);

  const [gridViewEventFilters, setGridViewEventFilters] = useState<GridViewEventFilters>({});

  const gridTimeslotFilterKey = (slot: Pick<ReserveTimelineItem, 'time' | 'duration'>) =>
    buildTimeSlotKeyPart(slot.time, slot.duration);

  const gridViewTableGroupItems = useMemo(() => {
    if (viewMode !== 'grid') return [];
    if (filters.selectedExperiences.length < 1) return [];

    const eventsRaw = ongoings
      .flatMap(ongoing => ongoing.events)
      .filter(ev =>
        filters.selectedExperiences.some(se => safeIdCompare(ev.template._id, se.item._id))
      );

    const allEvents = firstOf(eventsRaw, e => e.template._id).sort((a, b) =>
      a.template.title.localeCompare(b.template.title)
    );

    // create a map that with a given event template id I can get a unique list
    // of all possible timeslots across all the days for that event template
    const eventsByTemplateId = groupBy(eventsRaw, ev => ev.template._id);

    const hostFilter = (s: ReserveTimelineItem) =>
      filters.selectedHosts.length > 0
        ? filters.selectedHosts.some(sh => safeIdCompare(sh.item?._id, s.host))
        : true;

    const timeslotsByTemplateId = mapBy(
      Object.entries(eventsByTemplateId).map(([eventTemplateId, events]) => {
        const seenSlotKeys = new Set<string>();
        const uniqueTimeslots = events
          .flatMap(event => event.items.filter(hostFilter))
          .filter(slot => {
            const slotKey = slotKeyFromReserveTimelineItem(slot);
            if (seenSlotKeys.has(slotKey)) return false;
            seenSlotKeys.add(slotKey);
            return true;
          });

        return {
          eventTemplateId,
          timeslots: uniqueTimeslots,
        };
      }),
      'eventTemplateId'
    );

    const items = allEvents.map(ev => {
      const allTimeslotsOfEvent = timeslotsByTemplateId[ev.template._id]?.timeslots ?? [];
      const slotsForEvent =
        eventsByTemplateId[ev.template._id]?.flatMap(e => e.items.filter(hostFilter)) ?? [];
      const rentalsDict = buildRentalsDict(ev, allTimeslotsOfEvent);
      const orderedTimeslotsOfEvent = allTimeslotsOfEvent;

      const filteredTimeslots =
        gridViewEventFilters[ev.template._id]?.timeslotIds.length > 0
          ? orderedTimeslotsOfEvent.filter(slot =>
              gridViewEventFilters[ev.template._id].timeslotIds.includes(
                gridTimeslotFilterKey(slot)
              )
            )
          : orderedTimeslotsOfEvent;

      const uniqueTimeslotFilterSlots = Object.values(
        mapBy(orderedTimeslotsOfEvent, slot => gridTimeslotFilterKey(slot))
      );

      // pre-group this event's slots by date once, instead of re-filtering slotsForEvent
      // for every (timeslot, date) pair below
      const slotsForEventByDate = groupBy(slotsForEvent, s => dateToIsoUTC(s.date));
      const tableItemsBySlotKeyByDate = Object.fromEntries(
        Object.entries(slotsForEventByDate).map(([date, slotsForDate]) => [
          date,
          mapBy(makeListViewTableItem(slotsForDate, ev, date, rentalsDict), item =>
            slotKeyFromReserveTimelineItem(item._slot)
          ),
        ])
      );

      const oItem: GridViewTableItemGroup = {
        id: ev.template._id,
        name: ev.template.title,
        color: hashStringToColor(ev.template.title),
        allTimeslotFilterOptions: uniqueTimeslotFilterSlots.map(slot => {
          return {
            id: gridTimeslotFilterKey(slot),
            label: buildReserveTimeslotLabel({
              label: slot.label,
              time: slot.time,
              timeDisplay: slot.timeDisplay,
              duration: slot.duration,
              dayspan: slot.dayspan,
            }),
          };
        }),
        items: filteredTimeslots.map((slot, slotIdx) => {
          const slotKey = slotKeyFromReserveTimelineItem(slot);
          const dateCells = gridDates.reduce<{ [key: string]: ListViewTableItem | undefined }>(
            (acc, date) => {
              const item = tableItemsBySlotKeyByDate[date]?.[slotKey];
              if (item) acc[date] = item;
              return acc;
            },
            {}
          );

          return {
            id: `${ev.template._id}-${slotKeyFromReserveTimelineItem(slot)}`,
            time: buildReserveTimeslotLabel({
              time: slot.time,
              timeDisplay: slot.timeDisplay,
              duration: slot.duration,
              dayspan: slot.dayspan,
            }),
            label: slot.label,
            isLastInGroup:
              slotIdx === filteredTimeslots.length - 1 ||
              slot.rental !== filteredTimeslots[slotIdx + 1].rental,
            host:
              slot.host && usersDict[slot.host]
                ? makeFullName(usersDict[slot.host].firstName, usersDict[slot.host].lastName)
                : undefined,
            rental: slot.rental ? rentalsDict[slot.rental] : undefined,
            ...dateCells,
          };
        }),
      };

      return oItem;
    });

    return items.filter(group => group.items.length > 0);
  }, [ongoings, filters, gridDates, viewMode, gridViewEventFilters]);

  const activeDates = useMemo(() => {
    if (!filters.hideEmptyDays || viewMode !== 'grid') return gridDates;
    return gridDates.filter(date =>
      gridViewTableGroupItems.some(group =>
        group.items.some(row => gridViewCellIsListViewTableItem(row[date]))
      )
    );
  }, [gridDates, gridViewTableGroupItems, filters.hideEmptyDays, viewMode]);

  const HOST_COLUMN_WIDTH_REM = 12;
  const TIME_COLUMN_WIDTH_REM = 12;
  const DATE_COLUMN_WIDTH_REM = 9;
  const LAST_DATE_COLUMN_WIDTH_REM = 10;

  const gridTableWidth =
    HOST_COLUMN_WIDTH_REM +
    TIME_COLUMN_WIDTH_REM +
    (activeDates.length - 1) * DATE_COLUMN_WIDTH_REM +
    LAST_DATE_COLUMN_WIDTH_REM;

  const gridViewTableFields: GridViewTableFields[] = [
    {
      key: 'host',
      label: 'Rental / Host',
      tdProps: { style: { width: `${HOST_COLUMN_WIDTH_REM}rem` } },
      thProps: { style: { width: `${HOST_COLUMN_WIDTH_REM}rem` } },
    },
    {
      key: 'time',
      tdProps: { style: { width: `${TIME_COLUMN_WIDTH_REM}rem` } },
      thProps: { style: { width: `${TIME_COLUMN_WIDTH_REM}rem` } },
    },
    ...activeDates.map((date, index) => ({
      key: date,
      label: getLocaleDateStringFromIso(date, {
        weekday: 'short',
        month: 'numeric',
        day: 'numeric',
      }).toUpperCase(),
      tdProps: {
        style: {
          width: `${
            index === activeDates.length - 1 ? LAST_DATE_COLUMN_WIDTH_REM : DATE_COLUMN_WIDTH_REM
          }rem`,
        },
      },
      thProps: {
        style: {
          width: `${
            index === activeDates.length - 1 ? LAST_DATE_COLUMN_WIDTH_REM : DATE_COLUMN_WIDTH_REM
          }rem`,
        },
      },
    })),
  ];

  const gridViewTableTemplates: BubbleTableTemplate<GridViewTableItem>[] = [
    {
      key: 'host',
      tdProps: makeDefaultGridViewTemplateTdProps,
      render: ({ host, rental }) =>
        (host || rental) && (
          <div>
            {rental && <div>{rental}</div>}
            <div className={`${rental ? 'text-xs text-slate' : ''}`}>{host}</div>
          </div>
        ),
    },
    {
      key: 'time',
      tdProps: makeDefaultGridViewTemplateTdProps,
      render: ({ time, label }) => (
        <div>
          {label && <div>{label}</div>}
          <div className={`${label ? 'text-xs text-slate' : ''}`}>{time}</div>
        </div>
      ),
    },
    ...activeDates.map(date => ({
      key: date,
      tdProps: makeDefaultGridViewTemplateTdProps,
      render: (row: GridViewTableItem) => {
        const val = row[date];
        return val && gridViewCellIsListViewTableItem(val) ? (
          <div className="flex items-center">
            {canManageTimelineItem(user, val._slot) && (
              <BubbleOverlay loading={changingException} hideSpinner loadingClassName="rounded">
                <BubbleButton
                  onClick={() => handleToggleException(val.date, val._event, val._slot)}
                  className="p-2-i"
                  variant={val.isExceptionDate ? 'crimson' : 'slate'}
                  inverse
                  icon="ban"
                />
              </BubbleOverlay>
            )}
            <BubbleProgressBar
              current={val.seatsTaken}
              linearFill="lime"
              max={val.capacity}
              unknown={val.dayspanCapacityIsInaccurate}
              striped={val.seatsTaken > 0 && val.directSeatsTaken === 0 && val.capacity != 0}
              allowOverfill={val.allowOverfill}
              active={expandedRow?.rowKey === row.id && expandedRow?.date === date}
              className="cursor-pointer stretched-link flex-grow"
              style={{ width: '80px' }}
              onClick={() => {
                toggleExpandedRow(row.id, date);
                setShowReservationList(true);
              }}
            />
          </div>
        ) : null;
      },
    })),
  ];

  function makeDefaultGridViewTemplateTdProps(
    data: GridViewTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const isExpanded = expandedRow?.rowKey === data.id;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        isExpanded ? 'bg-bubblegum-lightest' : undefined,
        data.isLastInGroup && !isExpanded ? 'border-b-2 border-turquoise' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  const reservationTableFields = makeReservationTableFields();

  reservationTableFields.splice(2, 0, {
    key: 'attendance',
    label: 'Turnout',
  });

  const reservationTableTemplates: BubbleTableTemplate<ReservationTableItem>[] = [
    ...makeReservationTableTemplates({
      changingStatus,
      setChangingStatus,
      showDelete,
      setShowDelete,
      fetchData,
      onEdit: res => handleOpenReservationPanel({ type: 'edit', edit: res }),
    }),
    {
      key: 'attendance',
      render: data => (
        <BubbleCheckboxGroup
          value={data.attendance}
          vertical
          variant={opt => (opt === 'attended' ? 'lime' : opt === 'no-show' ? 'crimson' : 'navy')}
          allowEmpty
          options={['attended', 'no-show'] as ReservationAttendance[]}
          isSingleMode
          labelExtractor={opt => opt?.toUpperCase() || ''}
          readOnly={!canManageReservation(user, data._reservation)}
          optionAs="button"
          size="small"
          containerClassName="gap-1-i"
          className="p-1-i uppercase font-bold-i"
          onValue={s => handleChangeResAttendance(data._reservation, s)}
        />
      ),
    },
  ];

  const tallyTableFields: BubbleTableFields<ReservationTallyItem>[] = [
    { key: 'name' },
    { key: 'total' },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ];

  function tallyTableTemplates(
    setTally: React.Dispatch<React.SetStateAction<string | undefined>>
  ): BubbleTableTemplate<ReservationTallyItem>[] {
    return [
      {
        key: 'name',
        tdProps: makeDefaultTallyTemplateTdProps,
      },
      {
        key: 'total',
        render: item => (
          <BubbleProgressBar current={item.total} max={item.typeTotal} linearFill="lime" />
        ),
        tdProps: makeDefaultTallyTemplateTdProps,
      },
      {
        key: 'actions',
        tdProps: makeDefaultTallyTemplateTdProps,
        render: item => (
          <>
            <BubbleButton
              variant="navy"
              inverse
              className="p-0-i"
              icon={item.isExpanded ? 'chevron-up' : 'chevron-down'}
              onClick={item.isExpanded ? () => setTally(undefined) : () => setTally(item.id)}
            />
          </>
        ),
      },
    ];
  }

  function makeDefaultTallyTemplateTdProps(_: ReservationTallyItem) {
    return {
      className: ``,
    };
  }

  const [expandedTicketTallyKey, setExpandedTicketTallyKey] =
    useState<string | undefined>(undefined);
  const [expandedPackageTallyKey, setExpandedPackageTallyKey] =
    useState<string | undefined>(undefined);
  const [expandedAddonTallyKey, setExpandedAddonTallyKey] = useState<string | undefined>(undefined);

  function tallyTableItems(
    type: 'addons' | 'tickets' | 'packages',
    analytics: ReservationAnalytics
  ): ReservationTallyItem[] {
    switch (type) {
      case 'tickets':
        return analytics.tally.tickets
          .map(t => ({
            id: t.id,
            name: t.name,
            total: t.total,
            typeTotal: analytics.numOfTickets,
            isExpanded: expandedTicketTallyKey === t.id,
            _tally: t,
          }))
          .sort((a, b) => b.total - a.total);
      case 'addons':
        return analytics.tally.addons
          .map(t => ({
            id: t.id,
            name: t.name,
            total: t.total,
            typeTotal: analytics.numOfAddons,
            isExpanded: expandedAddonTallyKey === t.id,
            _tally: t,
          }))
          .sort((a, b) => b.total - a.total);

      case 'packages':
        return analytics.tally.packages
          .map(t => ({
            id: t.id,
            name: t.name,
            total: t.total,
            typeTotal: analytics.numOfPackages,
            isExpanded: expandedPackageTallyKey === t.id,
            _tally: t,
          }))
          .sort((a, b) => b.total - a.total);
    }
  }

  function tallyTableDetailsRender(item: ReservationTallyItem) {
    return (
      <table className="w-full border shadow-sm">
        <tbody>
          {item._tally.lines.map((res, i) => (
            <tr key={i}>
              <td className="p-1 w-0">{res.quantity}</td>
              <td className="p-1">{res.customer}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function handleSubmitReservation(newReservation: Reservation) {
    fetchData().catch(console.error);
    handleOpenReservationPanel({
      type: 'edit',
      edit: newReservation,
      goToPayments: true,
    });
  }

  async function handleToggleException(
    date: string,
    event: BareEventTemplate,
    slot: ReserveTimelineItem
  ) {
    setChangingException(true);
    try {
      await toggleSlotException(activeProperty._id, event._id, {
        date,
        ruleId: slot.ruleId,
        slotId: slot.slotId,
        rental: slot.rental,
      });
      await fetchData();
    } finally {
      setChangingException(false);
    }
  }

  async function handleChangeResAttendance(
    reservation: Reservation,
    newStatus: ReservationAttendance | undefined
  ) {
    const newOngoings = [...ongoings];

    // soft update reservation in table without refetching everything
    newOngoings.forEach(ongoing => {
      ongoing.events.forEach(ev => {
        ev.items.forEach(slot => {
          slot.directReservations.forEach(res => {
            if (safeIdCompare(res._id, reservation._id)) {
              res.attendance = newStatus;
            }
          });
        });
      });
    });

    setOngoings(newOngoings);

    changeAttendance(reservation, newStatus);
  }

  // TODO: turn this into a glance, and make it usable via notifications
  function listViewTableDetailsRender(item: ListViewTableItem, viewMode: 'list' | 'grid') {
    const ticketTallyTemplates = tallyTableTemplates(setExpandedTicketTallyKey);
    const packageTallyTemplates = tallyTableTemplates(setExpandedPackageTallyKey);
    const addonTallyTemplates = tallyTableTemplates(setExpandedAddonTallyKey);

    const ticketTallyItems = tallyTableItems('tickets', item.analytics);
    const packageTallyItems = tallyTableItems('packages', item.analytics);
    const addonTallyItems = tallyTableItems('addons', item.analytics);

    const matrix = item._rentalMatrix;
    const { columnOpts, rowOpts, cellMatrix } = matrix || buildRentalMatrix([]);
    const activeMatrixCell = activeMatrixCellCoord
      ? cellMatrix[activeMatrixCellCoord.rowKey]?.[activeMatrixCellCoord.colKey]
      : undefined;

    // can have duplicate reservations across the different cells that show up in the reservation list
    // this is an important dedupe step
    const shownReservations = firstOf(
      !activeMatrixCell ? item.reservations : activeMatrixCell.reservations,
      r => r.key
    );

    const TIME_COLUMN_WIDTH_REM = 12;
    const SLOT_COLUMN_WIDTH_REM = 8;
    const LAST_SLOT_COLUMN_WIDTH_REM = 9;

    const tableWidth =
      (columnOpts.length - 1) * SLOT_COLUMN_WIDTH_REM +
      TIME_COLUMN_WIDTH_REM +
      LAST_SLOT_COLUMN_WIDTH_REM;

    const rentalGridItems: RentalMatrixItem[] = rowOpts.map(r => ({
      id: r.key,
      time: r.label,
      timeLabel: r.timeLabel,
      host: r.hostLabel,
      isExceptionDate: r.isExceptionDate,
      slot: Object.values(cellMatrix[r.key] ?? {})[0]?.slot,
      cells: cellMatrix[r.key],
    }));

    function makeDefaultGridTemplateTdProps(
      _: RentalMatrixItem,
      fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
    ) {
      return {
        ...fieldTdProps,
        className: [].filter(notUndefined).join(' '),
      };
    }

    const rentalGridFields: BubbleTableFields<RentalMatrixItem>[] = [
      {
        key: 'timeHost',
        label: 'Time / Host',
        tdProps: {
          style: {
            width: `${TIME_COLUMN_WIDTH_REM}rem`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
        thProps: {
          style: {
            width: `${TIME_COLUMN_WIDTH_REM}rem`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
      },
      ...columnOpts.map((col, index) => ({
        key: col.key,
        label: col.label,
        tdProps: {
          className: 'overflow-ellipsis',
          style: {
            width: `${
              index === columnOpts.length - 1 ? LAST_SLOT_COLUMN_WIDTH_REM : SLOT_COLUMN_WIDTH_REM
            }rem`,
          },
        },
        thProps: {
          className: 'overflow-ellipsis',
          style: {
            width: `${
              index === columnOpts.length - 1 ? LAST_SLOT_COLUMN_WIDTH_REM : SLOT_COLUMN_WIDTH_REM
            }rem`,
          },
        },
      })),
    ];

    const rentalGridTemplates: BubbleTableTemplate<RentalMatrixItem>[] = [
      {
        key: 'timeHost',
        tdProps: makeDefaultGridTemplateTdProps,
        render: data => (
          <div>
            {data.timeLabel && <div>{data.timeLabel}</div>}
            <div className={`${data.timeLabel ? 'text-xs text-slate' : ''}`}>{data.time}</div>
            {data.host && <div className="text-xs text-slate">{data.host}</div>}
          </div>
        ),
      },
      ...columnOpts.map(col => ({
        key: col.key,
        tdProps: makeDefaultGridTemplateTdProps,
        render: (data: RentalMatrixItem) => {
          const cell = data.cells[col.key];
          if (!cell) return undefined;

          const isActiveCell =
            activeMatrixCellCoord?.rowKey === data.id && activeMatrixCellCoord?.colKey === col.key;

          return (
            <div className="flex items-center">
              {cell.slot && canManageTimelineItem(user, cell.slot) && (
                <BubbleOverlay loading={changingException} hideSpinner loadingClassName="rounded">
                  <BubbleButton
                    onClick={() => handleToggleException(item.date, item._event, cell.slot!)}
                    className="p-2-i"
                    variant={cell.isExceptionDate ? 'crimson' : 'slate'}
                    inverse
                    icon="ban"
                  />
                </BubbleOverlay>
              )}
              <BubbleProgressBar
                current={cell.seatsTaken}
                linearFill="lime"
                allowOverfill={cell.allowOverfill}
                striped={cell.seatsTaken > 0 && cell.directSeatsTaken === 0 && cell.capacity != 0}
                max={cell.capacity}
                active={isActiveCell}
                className="cursor-pointer flex-grow"
                onClick={() => {
                  setActiveMatrixCellCoord(prev =>
                    prev?.rowKey === data.id && prev?.colKey === col.key
                      ? undefined
                      : {
                          rowKey: data.id,
                          colKey: col.key,
                        }
                  );
                  setShowReservationList(true);
                }}
              />
            </div>
          );
        },
      })),
    ];

    return (
      <div
        className="px-4 pb-4"
        style={{
          maxWidth: `calc(100vw - ${viewMode === 'list' ? 445 : 235}px)`,
          position: 'sticky',
          left: 0,
        }}
      >
        <div className="bg-white shadow p-4 rounded">
          <div className="grid gap-4 grid-cols-3 justify-items-start">
            <div className="bg-white shadow-md w-full rounded overflow-hidden">
              <div className="px-3 py-2 bg-smoke">
                <h1 className="font-bold uppercase text-xs text-white text-right">
                  Ticket Summary
                </h1>
              </div>
              <BubbleTable
                responsive
                hideHeader
                hideShadow
                slim
                fields={tallyTableFields}
                templates={ticketTallyTemplates}
                items={ticketTallyItems.slice(0, showMoreTicketDetails ? undefined : 3)}
                detailsClassName="p-2"
                detailsRender={tallyTableDetailsRender}
                detailsKey={expandedTicketTallyKey}
                onRowClick={subItem =>
                  expandedTicketTallyKey === subItem.id
                    ? setExpandedTicketTallyKey(undefined)
                    : setExpandedTicketTallyKey(subItem.id)
                }
              />
              {!showMoreTicketDetails && ticketTallyItems.length > 3 && (
                <BubbleButton
                  variant="smoke"
                  block
                  inverse
                  className="col-span-3"
                  onClick={() => setShowMoreTicketDetails(true)}
                  icon="eye"
                >
                  Show more details
                </BubbleButton>
              )}
            </div>
            <div className="bg-white shadow-md w-full rounded overflow-hidden">
              <div className="px-3 py-2 bg-smoke">
                <h1 className="font-bold uppercase text-xs text-white text-right">
                  Package Summary
                </h1>
              </div>
              <BubbleTable
                responsive
                hideHeader
                hideShadow
                slim
                fields={tallyTableFields}
                templates={packageTallyTemplates}
                items={packageTallyItems.slice(0, showMorePackageDetails ? undefined : 3)}
                detailsClassName="p-2"
                detailsRender={tallyTableDetailsRender}
                detailsKey={expandedPackageTallyKey}
                onRowClick={subItem =>
                  expandedPackageTallyKey === subItem.id
                    ? setExpandedPackageTallyKey(undefined)
                    : setExpandedPackageTallyKey(subItem.id)
                }
              />
              {!showMorePackageDetails && packageTallyItems.length > 3 && (
                <BubbleButton
                  variant="smoke"
                  block
                  inverse
                  className="col-span-3"
                  onClick={() => setShowMorePackageDetails(true)}
                  icon="eye"
                >
                  Show more details
                </BubbleButton>
              )}
            </div>
            <div className="bg-white shadow-md w-full rounded overflow-hidden">
              <div className="px-3 py-2 bg-smoke">
                <h1 className="font-bold uppercase text-xs text-white text-right">Addon Summary</h1>
              </div>
              <BubbleTable
                responsive
                hideHeader
                hideShadow
                slim
                fields={tallyTableFields}
                templates={addonTallyTemplates}
                items={addonTallyItems.slice(0, showMoreAddonDetails ? undefined : 3)}
                detailsClassName="p-2"
                detailsRender={tallyTableDetailsRender}
                detailsKey={expandedAddonTallyKey}
                onRowClick={subItem =>
                  expandedAddonTallyKey === subItem.id
                    ? setExpandedAddonTallyKey(undefined)
                    : setExpandedAddonTallyKey(subItem.id)
                }
              />
              {!showMoreAddonDetails && addonTallyItems.length > 3 && (
                <BubbleButton
                  variant="smoke"
                  block
                  inverse
                  className="col-span-3"
                  onClick={() => setShowMoreAddonDetails(true)}
                  icon="eye"
                >
                  Show more details
                </BubbleButton>
              )}
            </div>
            {matrix && (
              <div
                className="col-span-3 overflow-x-auto mx-auto shadow"
                style={{ maxWidth: '100%' }}
              >
                <BubbleTable
                  fixed
                  style={{ width: `${tableWidth}rem` }}
                  headVariant="turquoise"
                  className="mx-auto"
                  fields={rentalGridFields}
                  items={rentalGridItems}
                  templates={rentalGridTemplates}
                />
              </div>
            )}
          </div>
          <div className="text-center relative mt-2">
            <BubbleButton
              variant="navy"
              className="stretched-link"
              inverse
              onClick={() => setShowReservationList(!showReservationList)}
              iconSuffix={showReservationList ? 'chevron-up' : 'chevron-down'}
            >
              {`${showReservationList ? 'Hide' : 'Show'} Reservations List`}
            </BubbleButton>
          </div>
          {showReservationList && (
            <div className="overflow-x-auto mt-2 w-full shadow">
              <BubbleTable
                responsive
                striped
                onRowClick={subItem => handleEditReservationById(subItem.sourceReservationId)}
                headVariant="slate"
                fields={reservationTableFields}
                templates={reservationTableTemplates}
                items={shownReservations}
                showPretitle
                pretitleClassName="p-0-i"
                pretitleRender={() => {
                  const slotForCreation = matrix ? activeMatrixCell?.slot : item._slot;
                  const canManage = slotForCreation
                    ? canManageTimelineItem(user, slotForCreation)
                    : false;
                  const slotDescription = matrix
                    ? activeMatrixCell?.description
                    : buildReserveTimeslotLabel({
                        label: item.label,
                        time: item._slot.time,
                        timeDisplay: item._slot.timeDisplay,
                        duration: item._slot.duration,
                        dayspan: item._slot.dayspan,
                        rental: item.rental,
                        host: item.host,
                      });

                  return (
                    <div className="px-4 pt-4 pb-1">
                      <div className="flex items-center">
                        {slotDescription && (
                          <div className="text-white mb-2">{slotDescription}</div>
                        )}
                        {slotForCreation && canManage && (
                          <BubbleButton
                            variant="white"
                            icon="plus"
                            className="border border-white p-1-i ml-3 mb-2"
                            inverse
                            onClick={() => {
                              handleOpenReservationPanel({
                                type: 'create',
                                slot: slotForCreation,
                                event: item._event,
                              });
                            }}
                          >
                            Add Reservation to This Slot
                          </BubbleButton>
                        )}
                      </div>
                      <BubbleInput
                        block
                        type="text"
                        variant="slate"
                        className="flex-grow"
                        placeholder="Search by name or reservation code..."
                        value={reservationSearch}
                        onValue={setReservationSearch}
                      />
                    </div>
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  const experienceOpts = firstOf(
    ongoings.map(o => o.events.map(e => e.template)).flat(),
    e => e._id
  ).map(ev => ({
    key: ev._id,
    item: ev,
  }));

  const hostOpts = users.map(u => ({
    key: u._id,
    item: u,
  }));

  return (
    <PageWrapper {...props}>
      <Page className="Reservables absolute flex items-stretch">
        <MenuBar {...props} property={activeProperty} />
        <div className="SideMenu bg-turquoise-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={activeProperty.brand.logo} />
            <PropertyBanner {...props} property={activeProperty} swappable />
          </div>
          <div className={`flex flex-col relative items-stretch p-4 bg-turquoise-darker`}>
            <div className="grid gap-2">
              {canCreateSomeReservations(user) && (
                <BubbleButton
                  variant="lime"
                  className="text-left"
                  icon="plus"
                  onClick={() => {
                    handleOpenReservationPanel({
                      type: 'create',
                    });
                  }}
                >
                  Add Reservation
                </BubbleButton>
              )}
              <div className="flex items-stretch rounded-lg overflow-hidden bg-turquoise border border-turquoise-darkest">
                <div className="flex-grow px-4 py-2">
                  <BubbleDebounce onTrigger={[(date: string) => handleOnChangeDate(date), 500]}>
                    {({ debounce }) => (
                      <BubbleInput
                        type="date"
                        block
                        appearance="bare"
                        inputClassName="bg-turquoise text-white inverse-indicator"
                        value={queryStrings.date}
                        onValue={debounce}
                      />
                    )}
                  </BubbleDebounce>
                </div>
                {queryStrings.date !== serverDate.iso && (
                  <BubbleButton
                    variant="turquoise"
                    appearance="squared"
                    className="-ml-4 pl-2-i"
                    icon="rotate-left"
                    onClick={() => handleOnChangeDate(serverDate.iso)}
                  />
                )}
              </div>
              {!filters.selectedDayOnly && (
                <div
                  className="grid grid-cols-3 items-stretch rounded-lg overflow-hidden"
                  style={{ gridTemplateColumns: 'auto 1fr auto' }}
                >
                  <BubbleButton
                    block
                    variant="slate"
                    appearance="squared"
                    onClick={() => handleOnAddDaysBefore(7)}
                    icon="arrow-left"
                  />
                  <div className="flex items-center justify-center text-white font-semibold bg-slate px-1">
                    Load Days
                  </div>
                  <BubbleButton
                    block
                    variant="slate"
                    appearance="squared"
                    onClick={() => handleOnAddDaysAfter(7)}
                    icon="arrow-right"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 rounded-lg overflow-hidden border border-white">
                <BubbleButton
                  block
                  variant="white"
                  inverse={viewMode === 'grid'}
                  appearance="squared"
                  icon="list"
                  onClick={() => setViewMode('list')}
                >
                  List
                </BubbleButton>
                <BubbleButton
                  block
                  variant="white"
                  inverse={viewMode === 'list'}
                  appearance="squared"
                  icon={['fass', 'chart-tree-map']}
                  onClick={() => setViewMode('grid')}
                >
                  Grid
                </BubbleButton>
              </div>
              <BubbleButton
                variant="white"
                inverse
                className="text-left border border-white"
                icon="repeat"
                onClick={() => fetchData().catch(console.error)}
              >
                Refresh Data
              </BubbleButton>
            </div>
          </div>
          <BubbleAccordion
            variant="turquoise"
            className="flex-grow flex flex-col border-t border-turquoise-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4 py-4">
              <BubbleFormGroup label="Experiences" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="turquoise"
                  placeholder="Filter experiences shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  value={filters.selectedExperiences}
                  labelExtractor={p => p.title}
                  options={experienceOpts}
                  onAdd={v =>
                    setFilters({
                      ...filters,
                      selectedExperiences: [...filters.selectedExperiences, v],
                    })
                  }
                  onRemove={v =>
                    setFilters({
                      ...filters,
                      selectedExperiences: filters.selectedExperiences.filter(
                        v2 => v2.key !== v.key
                      ),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedExperiences: [] })}
                />
                {viewMode === 'list' && (
                  <BubbleCheckbox
                    label="Group rows by event"
                    className="text-white mt-1"
                    variant="turquoise"
                    checked={filters.groupByEvent}
                    onValue={(checked: boolean) => {
                      setFilters({ ...filters, groupByEvent: checked });
                    }}
                  />
                )}
              </BubbleFormGroup>
              {canManageAllReservations(user) && (
                <BubbleFormGroup label="Host" labelClassName="text-white-i" className="px-4">
                  <BubbleTypeahead
                    block
                    variant="turquoise"
                    placeholder="Filter hosts shown..."
                    menuClassName="max-h-80 overflow-y-auto"
                    value={filters.selectedHosts}
                    options={hostOpts}
                    labelExtractor={p => (p ? makeFullName(p.firstName, p.lastName) : 'No Host')}
                    onAdd={v =>
                      setFilters({
                        ...filters,
                        selectedHosts: [...filters.selectedHosts, v],
                      })
                    }
                    onRemove={v =>
                      setFilters({
                        ...filters,
                        selectedHosts: filters.selectedHosts.filter(v2 => v2.key !== v.key),
                      })
                    }
                    onClearAll={() => setFilters({ ...filters, selectedHosts: [] })}
                  />
                </BubbleFormGroup>
              )}
              <BubbleFormGroup label="View Options" labelClassName="text-white-i" className="px-4">
                {viewMode === 'list' && (
                  <>
                    <BubbleCheckbox
                      label="Selected day only"
                      className="text-white mt-1"
                      variant="turquoise"
                      checked={filters.selectedDayOnly}
                      onValue={(checked: boolean) => {
                        setFilters({ ...filters, selectedDayOnly: checked });
                      }}
                    />
                    <BubbleCheckbox
                      label="Hide empty slots"
                      className="text-white mt-1"
                      variant="turquoise"
                      checked={filters.hideEmptySlots}
                      onValue={(checked: boolean) => {
                        setFilters({ ...filters, hideEmptySlots: checked });
                      }}
                    />
                  </>
                )}
                <BubbleCheckbox
                  label="Hide empty days"
                  className="text-white mt-1"
                  variant="turquoise"
                  checked={filters.hideEmptyDays}
                  onValue={(checked: boolean) => {
                    setFilters({ ...filters, hideEmptyDays: checked });
                  }}
                />
                <BubbleCheckbox
                  label="Omit cancelled"
                  className="text-white mt-1"
                  variant="turquoise"
                  checked={!filters.showArchived}
                  onValue={checked => {
                    setFilters({ ...filters, showArchived: !checked });
                  }}
                />
                <BubbleCheckbox
                  label="Omit released"
                  className="text-white mt-1"
                  variant="turquoise"
                  checked={!filters.showReleased}
                  onValue={checked => {
                    setFilters({ ...filters, showReleased: !checked });
                  }}
                />
                {/* <BubbleCheckbox
                  label="Show indirect reservations"
                  className="text-white mt-1"
                  variant="turquoise"
                  checked={filters.showIndirect}
                  onValue={checked => {
                    setFilters({ ...filters, showIndirect: checked });
                  }}
                /> */}
              </BubbleFormGroup>
            </div>
          </BubbleAccordion>
        </div>
        <div className="ReservablesContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="ReservablesHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <ReserveNavigation active="reserve" />
            </div>
            <div
              className="absolute w-full bg-blue-lightest flex border-blue-lightest -mx-5 px-5"
              style={{
                height: 'calc(40px + 0.75rem)',
                bottom: 'calc(-40px - 0.75rem)',
                borderBottomWidth: '0.75rem',
                zIndex: 10,
              }}
            >
              <ReserveSubNavigation active="calendar" property={activeProperty} />
            </div>
          </div>
          <div className="ReservablesBody mx-5 content-minus-logo-minus-bar min-w-900">
            <div
              style={{
                marginTop: 'calc(40px + 0.75rem)',
              }}
            >
              <BubbleOverlay loading={loading} loadingMinHeight="medium">
                {viewMode === 'list' ? (
                  <div>
                    {expandedListViewTableGroupItems.map(bti => (
                      <BubbleTable
                        key={bti.id}
                        className="mb-3"
                        responsive
                        striped
                        headVariant="turquoise"
                        fields={listViewTableFields}
                        items={bti.items}
                        templates={listViewTableTemplates}
                        stickyHeader={182}
                        onRowClick={item => {
                          toggleExpandedRow(item.id, item.date);
                        }}
                        detailsKey={expandedRow?.rowKey}
                        detailsRender={item => listViewTableDetailsRender(item, 'list')}
                        detailsClassName="p-0-i bg-bubblegum-lightest"
                        showPretitle
                        pretitleClassName="p-0-i"
                        pretitleRender={() => (
                          <div
                            className="px-5 py-3 font-bold text-white text-center"
                            style={{ backgroundColor: bti.color }}
                          >
                            {bti.date}
                          </div>
                        )}
                      />
                    ))}
                    {!expandedListViewTableGroupItems.length && !loading && (
                      <BubbleAlert variant="banana">No reservations to show</BubbleAlert>
                    )}
                    <div className="mt-3">&nbsp;</div>
                  </div>
                ) : (
                  <div>
                    {gridViewTableGroupItems.map(bti => (
                      <BubbleTable
                        key={bti.id}
                        className="mb-3"
                        fixed
                        style={{ width: `${gridTableWidth}rem`, minWidth: '100%' }}
                        striped
                        headVariant="turquoise"
                        fields={gridViewTableFields}
                        items={bti.items}
                        templates={gridViewTableTemplates}
                        detailsKey={expandedRow?.rowKey}
                        detailsRender={item => {
                          // this improves the speed at which we render the details component
                          const val = expandedRow && item[expandedRow.date];
                          if (gridViewCellIsListViewTableItem(val)) {
                            return listViewTableDetailsRender(val, 'grid');
                          }
                          return undefined;
                        }}
                        detailsClassName="p-0-i bg-bubblegum-lightest"
                        stickyHeader={182}
                        showPretitle
                        pretitleClassName="p-0-i"
                        pretitleRender={() => (
                          <GridViewPretitle
                            tableItem={bti}
                            gridViewEventFilters={gridViewEventFilters}
                            setGridViewEventFilters={setGridViewEventFilters}
                          />
                        )}
                      />
                    ))}
                    {!gridViewTableGroupItems.length && !loading && (
                      <BubbleAlert variant="banana">
                        {filters.selectedExperiences.length < 1
                          ? 'Select one or more experiences to show the grid'
                          : 'No reservations to show'}
                      </BubbleAlert>
                    )}
                    <div className="mt-3">&nbsp;</div>
                  </div>
                )}
              </BubbleOverlay>
            </div>
          </div>
        </div>
        {mode && (
          <BubbleDrawer containerClassName="bg-eggshell" onClose={handleCloseReservationPanel}>
            <ReservationPath
              mode={mode}
              users={users}
              onClose={handleCloseReservationPanel}
              onRefreshReservation={handleSubmitReservation}
              onNavigateToReservation={r => handleOpenReservationPanel({ type: 'edit', edit: r })}
            />
          </BubbleDrawer>
        )}
      </Page>
    </PageWrapper>
  );
}

type GridViewEventFilters = {
  [eventTemplateId: string]: {
    timeslotIds: string[];
  };
};
interface GridViewPretitleProps {
  tableItem: GridViewTableItemGroup;
  gridViewEventFilters: GridViewEventFilters;
  setGridViewEventFilters: React.Dispatch<React.SetStateAction<GridViewEventFilters>>;
}

function GridViewPretitle(props: GridViewPretitleProps) {
  const [open, setIsOpen] = useState(false);

  const selectedTimeslotIds = props.gridViewEventFilters[props.tableItem.id]?.timeslotIds ?? [];

  const timeslotLabelDict: Record<string, string> = Object.fromEntries(
    props.tableItem.allTimeslotFilterOptions.map(opt => [opt.id, opt.label])
  );

  const timeslotOptions = props.tableItem.allTimeslotFilterOptions.map(opt => ({
    key: opt.id,
    refKey: opt.id,
    item: opt.id,
  }));

  const selectedTimeslotValues: Array<BubbleTypeaheadValue<string>> = selectedTimeslotIds.map(
    id => ({
      key: id,
      refKey: id,
      item: id,
    })
  );

  const isFiltered = selectedTimeslotIds.length > 0;

  return (
    <div
      className="py-1 sticky"
      style={{
        backgroundColor: props.tableItem.color,
        maxWidth: 'calc(-230px + 100vw)',
        position: 'sticky',
        left: 0,
      }}
    >
      <div className="w-full text-center relative">
        <BubbleButton
          variant="white"
          inverse
          className="text-center stretched-link"
          onClick={() => setIsOpen(!open)}
          iconSuffix={open ? 'chevron-up' : 'chevron-down'}
        >
          {props.tableItem.name}
          {isFiltered ? ' (filtered)' : ''}
        </BubbleButton>
      </div>
      {open && (
        <div className="px-4 pb-4">
          <div
            className="bg-white p-4 text-black font-normal
          normal-case shadow-md rounded"
          >
            <div className="grid gap-4">
              <BubbleFormGroup label="Timeslots">
                <BubbleTypeahead
                  block
                  value={selectedTimeslotValues}
                  options={timeslotOptions}
                  labelExtractor={id => timeslotLabelDict[id] || id}
                  placeholder="Filter timeslots shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  onAdd={v => {
                    props.setGridViewEventFilters(prev => {
                      const curr = prev[props.tableItem.id]?.timeslotIds ?? [];
                      if (curr.includes(v.item)) return prev;

                      return {
                        ...prev,
                        [props.tableItem.id]: {
                          timeslotIds: [...curr, v.item],
                        },
                      };
                    });
                  }}
                  onRemove={v => {
                    props.setGridViewEventFilters(prev => {
                      const curr = prev[props.tableItem.id]?.timeslotIds ?? [];
                      const nextIds = curr.filter(id => id !== v.item);

                      if (nextIds.length < 1) {
                        const next = { ...prev };
                        delete next[props.tableItem.id];
                        return next;
                      }

                      return {
                        ...prev,
                        [props.tableItem.id]: {
                          timeslotIds: nextIds,
                        },
                      };
                    });
                  }}
                  onClearAll={() => {
                    props.setGridViewEventFilters(prev => {
                      const next = { ...prev };
                      delete next[props.tableItem.id];
                      return next;
                    });
                  }}
                />
              </BubbleFormGroup>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
